import {
	VictoryAxis,
	VictoryBar,
	VictoryChart,
	VictoryLabel,
	VictoryLegend,
	VictoryLine,
	VictoryScatter,
	VictoryTheme,
} from 'victory';
import classNames from 'classnames';
import { Link, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

import { fontFamilySecondary, getPercentage, hiddenAxisStyles, toFullDate } from 'utils';
import { GaugeChart, LogoOverlay, MetricPanel, PageHeading, Tooltip } from 'components';
import { useAppSelector, useGetSupplyChainDashboardQuery } from 'store';
import image from 'assets/images/empty.svg';
import { Routes } from 'models';
import { useToast } from 'context';

const test_data = {
	addons: {
		current: 27.0,
		previous: 29.0,
		target: '10',
		weekdays: [
			{
				current: 8.25,
				name: 'Monday',
				previous: 10.4,
			},
			{
				current: 10.25,
				name: 'Tuesday',
				previous: 8.4,
			},
			{
				current: 9.6,
				name: 'Wednesday',
				previous: 9.0,
			},
			{
				current: 11.8,
				name: 'Thursday',
				previous: 8.25,
			},
			{
				current: 12.75,
				name: 'Friday',
				previous: 10.75,
			},
		],
	},
	current_month: 'April',
	end_date: '04/30/2026',
	expense: {
		current: 49.0,
		previous: 46.0,
		target: '75',
		top_rooms: [
			{
				current: 1.42,
				name: 'Mar 1 - Mar 31',
			},
			{
				current: 1.24,
				name: 'April 1 - April 30',
			},
		],
	},
	off_contract: {
		current: 49.0,
		previous: 46.0,
		target: '75',
		top_rooms: [
			{
				current: 788,
				name: 'Mar 1 - Mar 31',
			},
			{
				current: 856,
				name: 'April 1 - April 30',
			},
		],
	},
	freight: {
		current: 49.0,
		previous: 46.0,
		target: '75',
		top_rooms: [
			{
				current: 2.32,
				name: 'Mar 1 - Mar 31',
			},
			{
				current: 2.45,
				name: 'April 1 - April 30',
			},
		],
	},
	missed_charges: {
		current: 49.0,
		previous: 46.0,
		target: '75',
		top_rooms: [
			{
				current: 276,
				name: 'Mar 1 - Mar 31',
			},
			{
				current: 312,
				name: 'April 1 - April 30',
			},
		],
	},
	overview_date_end: '04/30/2026',
	overview_date_start: '04/01/2026',
	previous_month: 'March',
	start_date: '03/01/2026',
};

const expense_data = [
	{ x: new Date(2025, 3, 1), y: 0 },
	{ x: new Date(2025, 3, 7), y: 1200000 },
	{ x: new Date(2025, 3, 14), y: 2400000 },
	{ x: new Date(2025, 3, 21), y: 1800000 },
	{ x: new Date(2025, 3, 28), y: 3500000 },
];

const off_contract_spend_data = [
	{ x: new Date(2025, 3, 1), y: 800000 },
	{ x: new Date(2025, 3, 7), y: 1600000 },
	{ x: new Date(2025, 3, 14), y: 2200000 },
	{ x: new Date(2025, 3, 21), y: 3000000 },
	{ x: new Date(2025, 3, 28), y: 2800000 },
];

const freight_expenses_data = [
	{ x: new Date(2025, 3, 1), y: 400000 },
	{ x: new Date(2025, 3, 7), y: 1200000 },
	{ x: new Date(2025, 3, 14), y: 1800000 },
	{ x: new Date(2025, 3, 21), y: 1500000 },
	{ x: new Date(2025, 3, 28), y: 1400000 },
];

const missed_charges_data = [
	{ x: new Date(2025, 3, 1), y: 380000 },
	{ x: new Date(2025, 3, 7), y: 1800000 },
	{ x: new Date(2025, 3, 14), y: 2400000 },
	{ x: new Date(2025, 3, 21), y: 3200000 },
	{ x: new Date(2025, 3, 28), y: 2400000 },
];

const spend_breakdown = [
	{ name: 'Transportation', x: 'Transportation', y: 1450000 },
	{ name: 'Packaging', x: 'Packaging', y: 620000 },
	{ name: 'Raw Materials', x: 'Raw Materials', y: 540000 },
	{ name: 'Labor', x: 'Labor', y: 410000 },
	{ name: 'Other', x: 'Other', y: 295000 },
];

export function SupplyChainDashboard() {
	const { selectedFacility } = useAppSelector((state) => state.userState);
	const { data, isFetching, error } = useGetSupplyChainDashboardQuery({ facility_id: selectedFacility });
	console.log(data);
	const navigate = useNavigate();
	// Handle api response to create toast message on every error.
	const { createToast } = useToast();
	// useEffect(() => {
	// 	if (error) {
	// 		if ('data' in error) {
	// 			createToast({
	// 				title: `${error.data}`,
	// 				variant: 'error',
	// 			});
	// 		} else {
	// 			createToast({
	// 				title: `There was an error connecting to the server.`,
	// 				variant: 'error',
	// 			});
	// 		}
	// 	}
	// }, [createToast, error]);
	/**
	 * It is possible for the API to return "null" values for many metrics, if the facility is missing data.
	 * Hence, the default state for much of the UI is to behave as if the API will return "empty" datasets, and only once
	 * we receive valid metrics do we show the UI with data.
	 */
	// let addonDifference = null;
	// let fcotsDifference = null;
	// let turnoverDifference = null;
	// if (data && data.previous_month) {
	// 	addonDifference = !!data.addons.previous && !!data.addons.current ? data.addons.current - data.addons.previous : null;
	// 	fcotsDifference = data.fcots.previous ? data.fcots.current - data.fcots.previous : null;
	// 	turnoverDifference = data.fcots.previous ? data.fcots.current - data.fcots.previous : null;
	// }
	const roomCount = 5;
	const barPosition = { from: roomCount, to: roomCount > 4 ? 5 : 0 };

	return (
		<div>
			<div className='flex justify-between'>
				<PageHeading>Supply Opportunity Snapshot</PageHeading>
				{/* 
					TODO: Implement soon.
					<Button sizeX='md' sizeY='md' variant='primary'>
						Export
					</Button>
				*/}
			</div>
			<div className='flex flex-col xl:flex-row'>
				<div className='w-full xl:w-9/10 xl:pr-4 relative'>
					{isFetching && <LogoOverlay backgroundColor='white' />}
					{data && (
						<>
							{/* <div className='flex flex-col xl:flex-row gap-8 my-8'>
								<div className='basis-full bg-gray-50 rounded-b-md'>
									<div className='h-16 flex items-left justify-left gap-3 p-3 bg-bluegray-900 text-white rounded-sm'>
										<div className=''>
											<div className='font-preset-label-white uppercase font-secondary text-p2'>Expense Reduction</div>
											<div className='flex gap-2 mt-0.5'></div>
											<div className='flex'>
												<div className={'text-green-500'}>
													<div className='material-symbol-[1rem]'>arrow_downward</div>
												</div>
												<div className='font-secondary text-[0.8em]'>12.4% vs prior period</div>
											</div>
										</div>
									</div>
									{data.expense.top_rooms.length ? (
										<div className='p-4'>
											<ChartHeading
												title={`$1.24M`}
												subtitle={'Savings from par level inventory optimization and obsolete/expired item reduction'}
												description={'placeholder'}
											/>
											{data.current_month && (
												<div>
													<VictoryChart>
														<VictoryAxis style={hiddenAxisStyles} />
														<VictoryAxis />
														<VictoryBar
															domain={{ x: [0, 3] }}
															barWidth={80}
															cornerRadius={2}
															labels={data.expense.top_rooms.map((r) => `$${r.current}M`)}
															style={{ data: { fill: 'hsl(215 93% 80%)' } }}
															data={data.expense.top_rooms.map((r) => ({
																x: r.name,
																y0: 0,
																y: r.current,
															}))}
															labelComponent={
																<VictoryLabel
																	verticalAnchor='middle'
																	dx={0}
																	textAnchor={'middle'}
																	style={{ fontFamily: fontFamilySecondary, fill: 'black' }}
																/>
															}
														/>
													</VictoryChart>
												</div>
											)}
										</div>
									) : (
										<div className='flex flex-col items-center'>
											<div className='flex flex-col w-full items-center gap-6 p-8 text-p2 text-gray-600 pt-20'>
												<img className='w-44' alt='An empty clipboard' src={image} />
												{'There are no data to display.'}
											</div>
										</div>
									)}
								</div>
								<div className='basis-full bg-gray-50 rounded-b-md'>
									<div className='h-16 flex items-left justify-left gap-3 p-3 bg-bluegray-900 text-white rounded-sm'>
										<div className=''>
											<div className='font-preset-label-white uppercase text-p2'>Off Contract Spend</div>
											<div className='flex gap-2 mt-0.5'></div>
											<div className='flex'>
												<div className={'text-red-500'}>
													<div className='material-symbol-[1rem]'>arrow_upward</div>
												</div>
												<div className='font-secondary text-[0.8em]'>8.7% vs prior period</div>
											</div>
										</div>
									</div>
									{data.off_contract.top_rooms.length ? (
										<div className='p-4'>
											<ChartHeading
												title={`$856K`}
												subtitle={'Overview of spend with suppliers outside of contract terms'}
												description={'placeholder'}
											/>
											{data.current_month && (
												<div>
													<VictoryChart>
														<VictoryAxis style={hiddenAxisStyles} />
														<VictoryAxis />
														<VictoryBar
															domain={{ x: [0, 3] }}
															barWidth={80}
															cornerRadius={2}
															labels={data.off_contract.top_rooms.map((r) => `$${r.current}K`)}
															style={{ data: { fill: 'hsl(215 93% 80%)' } }}
															data={data.off_contract.top_rooms.map((r) => ({
																x: r.name,
																y0: 0,
																y: r.current,
															}))}
															labelComponent={
																<VictoryLabel
																	verticalAnchor='middle'
																	dx={0}
																	textAnchor={'middle'}
																	style={{ fontFamily: fontFamilySecondary, fill: 'black' }}
																/>
															}
														/>
													</VictoryChart>
												</div>
											)}
										</div>
									) : (
										<div className='flex flex-col items-center'>
											<div className='flex flex-col w-full items-center gap-6 p-8 text-p2 text-gray-600 pt-20'>
												<img className='w-44' alt='An empty clipboard' src={image} />
												{'There are no data to display.'}
											</div>
										</div>
									)}
								</div>
								<div className='basis-full bg-gray-50 rounded-b-md'>
									<div className='h-16 flex items-left justify-left gap-3 p-3 bg-bluegray-900 text-white rounded-sm'>
										<div className=''>
											<div className='font-preset-label-white uppercase font-secondary text-p2'>Freight Expenses</div>
											<div className='flex gap-2 mt-0.5'></div>
											<div className='flex'>
												<div className={'text-red-500'}>
													<div className='material-symbol-[1rem]'>arrow_upward</div>
												</div>
												<div className='font-secondary text-[0.8em]'>5.6% vs prior period</div>
											</div>
										</div>
									</div>
									{data.freight.top_rooms.length ? (
										<div className='p-4'>
											<ChartHeading
												title={`$2.45M`}
												subtitle={'Total freight spend including inbound and outbound'}
												description={'placeholder'}
											/>
											{data.current_month && (
												<div>
													<VictoryChart>
														<VictoryAxis style={hiddenAxisStyles} />
														<VictoryAxis />
														<VictoryBar
															domain={{ x: [0, 3] }}
															barWidth={80}
															cornerRadius={2}
															labels={data.freight.top_rooms.map((r) => `$${r.current}M`)}
															style={{ data: { fill: 'hsl(215 93% 80%)' } }}
															data={data.freight.top_rooms.map((r) => ({
																x: r.name,
																y0: 0,
																y: r.current,
															}))}
															labelComponent={
																<VictoryLabel
																	verticalAnchor='middle'
																	dx={0}
																	textAnchor={'middle'}
																	style={{ fontFamily: fontFamilySecondary, fill: 'black' }}
																/>
															}
														/>
													</VictoryChart>
												</div>
											)}
										</div>
									) : (
										<div className='flex flex-col items-center'>
											<div className='flex flex-col w-full items-center gap-6 p-8 text-p2 text-gray-600 pt-20'>
												<img className='w-44' alt='An empty clipboard' src={image} />
												{'There are no data to display.'}
											</div>
										</div>
									)}
								</div>
								<div className='basis-full bg-gray-50 rounded-b-md'>
									<div className='h-16 flex items-left justify-left gap-3 p-3 bg-bluegray-900 text-white rounded-sm'>
										<div className=''>
											<div className='font-preset-label-white uppercase font-secondary text-p2'>Missed Charges</div>
											<div className='flex gap-2 mt-0.5'></div>
											<div className='flex'>
												<div className={'text-red-500'}>
													<div className='material-symbol-[1rem]'>arrow_upward</div>
												</div>
												<div className='font-secondary text-[0.8em]'>13.2% vs prior period</div>
											</div>
										</div>
									</div>
									{data.missed_charges.top_rooms.length ? (
										<div className='p-4'>
											<ChartHeading
												title={`$312K`}
												subtitle={'Snapshot of revenue loss due to unbilled or missed charges'}
												description={'placeholder'}
											/>
											{data.current_month && (
												<div>
													<VictoryChart>
														<VictoryAxis style={hiddenAxisStyles} />
														<VictoryAxis />
														<VictoryBar
															domain={{ x: [0, 3] }}
															barWidth={80}
															cornerRadius={2}
															labels={data.missed_charges.top_rooms.map((r) => `$${r.current}K`)}
															style={{ data: { fill: 'hsl(215 93% 80%)' } }}
															data={data.missed_charges.top_rooms.map((r) => ({
																x: r.name,
																y0: 0,
																y: r.current,
															}))}
															labelComponent={
																<VictoryLabel
																	verticalAnchor='middle'
																	dx={0}
																	textAnchor={'middle'}
																	style={{ fontFamily: fontFamilySecondary, fill: 'black' }}
																/>
															}
														/>
													</VictoryChart>
												</div>
											)}
										</div>
									) : (
										<div className='flex flex-col items-center'>
											<div className='flex flex-col w-full items-center gap-6 p-8 text-p2 text-gray-600 pt-20'>
												<img className='w-44' alt='An empty clipboard' src={image} />
												{'There are no data to display.'}
											</div>
										</div>
									)}
								</div>
							</div>
							<div className='flex flex-col xl:flex-row gap-8 my-8'>
								<div className='basis-full bg-gray-50 rounded-b-md'>
									{data.expense.top_rooms.length ? (
										<div className='p-4'>
											<ChartHeading title={`Trends over Time`} description='' />
											{data.current_month && (
												<div>
													<VictoryChart height={340}>
														<VictoryLegend
															x={20}
															y={5}
															orientation='horizontal'
															data={[
																{
																	name: 'Expense Reduction (savings)',
																	symbol: { fill: '#154B4c' },
																},
																{
																	name: 'Off Contract Spend',
																	symbol: { fill: '#9dc4fb' },
																},
																{
																	name: 'Freight Expenses',
																	symbol: { fill: '#245294' },
																},
																{
																	name: 'Missed Charges',
																	symbol: { fill: '#8d8787' },
																},
															]}
															style={{
																title: { fontSize: 8, fontFamily: fontFamilySecondary, fill: '#333' },
																labels: { fontSize: 8, fontFamily: 'Arial', fill: '#666' },
																data: { fontSize: 8 }, // Impacts padding and layout of items
																border: { stroke: '#ccc', strokeWidth: 1 },
															}}
														/>
														<VictoryAxis
															tickValues={[
																new Date(2025, 3, 1),
																new Date(2025, 3, 7),
																new Date(2025, 3, 14),
																new Date(2025, 3, 21),
																new Date(2025, 3, 28),
															]}
															tickFormat={(date) =>
																date.toLocaleDateString('en-US', {
																	month: 'short',
																	day: 'numeric',
																})
															}
															style={{
																axis: { stroke: '#756f6a', strokeWidth: 2 },
																axisLabel: { fontSize: 8, fontFamily: fontFamilySecondary, padding: 30 },
																tickLabels: { fontSize: 8, fontFamily: fontFamilySecondary, fill: 'black' },
															}}
														/>
														<VictoryAxis
															dependentAxis
															tickValues={[0, 1000000, 2000000, 3000000, 4000000]}
															tickFormat={(y) => (y === 0 ? '0' : `$${y / 1000000}M`)}
															style={{
																axis: { stroke: '#756f6a', strokeWidth: 2 },
																axisLabel: { fontSize: 8, fontFamily: fontFamilySecondary, padding: 30 },
																tickLabels: { fontSize: 8, fontFamily: fontFamilySecondary, fill: 'black' },
																grid: { stroke: '#e0e0e0', strokeWidth: 1 }, // Customize grid line
															}}
														/>
														<VictoryLine
															data={expense_data}
															style={{
																data: { stroke: '#154B4c' },
															}}
														/>
														<VictoryScatter data={expense_data} size={2} style={{ data: { fill: '#154B4c' } }} />
														<VictoryLine
															data={off_contract_spend_data}
															style={{
																data: { stroke: '#9dc4fb' },
															}}
														/>
														<VictoryScatter data={off_contract_spend_data} size={2} style={{ data: { fill: '#9dc4fb' } }} />
														<VictoryLine
															data={freight_expenses_data}
															style={{
																data: { stroke: '#245294' },
															}}
														/>
														<VictoryScatter data={freight_expenses_data} size={2} style={{ data: { fill: '#245294' } }} />
														<VictoryLine
															data={missed_charges_data}
															style={{
																data: { stroke: '#8d8787' },
															}}
														/>
														<VictoryScatter data={missed_charges_data} size={2} style={{ data: { fill: '#8d8787' } }} />
													</VictoryChart>
												</div>
											)}
										</div>
									) : (
										<div className='flex flex-col items-center'>
											<div className='flex flex-col w-full items-center gap-6 p-8 text-p2 text-gray-600 pt-20'>
												<img className='w-44' alt='An empty clipboard' src={image} />
												{'There are no data to display.'}
											</div>
										</div>
									)}
								</div>
								<div className='basis-full bg-gray-50 rounded-b-md'>
									{data.addons.weekdays.length > 0 ? (
										<div className='p-4'>
											<ChartHeading
												title='Spend by Category (Top 5)'
												description='Average number of add-on cases expected by day of week for the time period listed. Don’t see anything here? Your EHR may not label add-ons on a case-by-case basis.'
											/>
											{data.current_month && (
												<div>
													<VictoryChart padding={{ left: 110, right: 50, top: 50, bottom: 50 }}>
														<VictoryAxis style={hiddenAxisStyles} tickValues={spend_breakdown.map((d) => d.x)} />
														<VictoryAxis
															style={{
																axis: { stroke: 'transparent' },
																tickLabels: { fontSize: 12, fontFamily: fontFamilySecondary, fill: 'black' },
															}}
														/>
														<VictoryBar
															domain={{ x: [barPosition.from, barPosition.to] }}
															horizontal
															barWidth={45}
															cornerRadius={2}
															labels={spend_breakdown.map((r) => `$${r.y / 1000000}M`)}
															style={{ data: { fill: 'hsl(215 93% 80%)' } }}
															data={spend_breakdown}
															labelComponent={
																<VictoryLabel
																	verticalAnchor='middle'
																	dx={-10}
																	textAnchor={'end'}
																	style={{ fontSize: 10, fontFamily: fontFamilySecondary, fill: 'black' }}
																/>
															}
														/>
													</VictoryChart>
												</div>
											)}
										</div>
									) : (
										<div className='flex flex-col items-center'>
											<div className='flex flex-col w-full items-center gap-6 p-8 text-p2 text-gray-600 pt-20'>
												<img className='w-44' alt='An empty clipboard' src={image} />
												{'There are no data to display.'}
											</div>
										</div>
									)}
								</div>
								<div className='basis-full bg-gray-50 rounded-b-md hidden'>
									<MetricPanel
										left={{
											type: 'gauge',
											chartColor: 'addon',
											label: 'Schedule Feed Record Type Frequency',
											value: data.addons.weekdays.length > 0 ? data.addons.current : '--',
											metric: data.addons.weekdays.length > 0 ? '%' : undefined,
										}}
										right={{
											type: 'gauge',
											label: 'Target Add-On Rate',
											value: data.addons.weekdays.length > 0 ? data.addons.target : '--',
											metric: data.addons.weekdays.length > 0 ? '%' : undefined,
										}}
									/>
									{data.addons.weekdays.length > 0 ? (
										<div className='p-4'>
											<ChartHeading
												title='Average Daily Add-Ons'
												description='Average number of add-on cases expected by day of week for the time period listed. Don’t see anything here? Your EHR may not label add-ons on a case-by-case basis.'
											/>
											{data.current_month && (
												<div>
													<VictoryChart>
														<VictoryAxis style={hiddenAxisStyles} />
														<VictoryAxis style={hiddenAxisStyles} />
														<VictoryBar
															domain={{ x: [barPosition.from, barPosition.to] }}
															horizontal
															barWidth={45}
															cornerRadius={2}
															labels={data.expense.top_rooms.map((r) => `${r.name} - ${r.current}%`)}
															style={{ data: { fill: 'hsl(215 93% 80%)' } }}
															data={data.expense.top_rooms.map((r) => ({
																x: r.name,
																y0: 0,
																y: r.current,
															}))}
															labelComponent={
																<VictoryLabel
																	verticalAnchor='middle'
																	dx={-10}
																	textAnchor={'end'}
																	style={{ fontFamily: fontFamilySecondary, fill: 'black' }}
																/>
															}
														/>
													</VictoryChart>
												</div>
											)}
										</div>
									) : (
										<div className='flex flex-col items-center'>
											<div className='flex flex-col w-full items-center gap-6 p-8 text-p2 text-gray-600 pt-20'>
												<img className='w-44' alt='An empty clipboard' src={image} />
												{'There are no data to display.'}
											</div>
										</div>
									)}
								</div>
							</div> */}
							<div className='bg-blue-50 p-6'>
								<p>
									{data ? (
										<>
											<span className='font-semibold mr-4'>Total Supply Spend</span>
											{/* should probably make this a reusable "badge" component, used pretty often */}
											<span className='bg-blue-500 text-white mx-1 px-2.5 py-0.5 rounded font-secondary text-p2'>
												{' '}
												{data &&
													data.total_supply_spend.toLocaleString('en-US', {
														style: 'currency',
														currency: 'USD',
														maximumFractionDigits: 0,
													})}
											</span>
										</>
									) : (
										<span className='font-semibold mr-4'>This facility does not have any periods to analyze.</span>
									)}
								</p>
							</div>
							<div className='bg-blue-50 p-6 mt-4'>
								<p>
									{data ? (
										<>
											<span className='font-semibold mr-4'>Current Inventory Cost</span>
											{/* should probably make this a reusable "badge" component, used pretty often */}
											<span className='bg-blue-500 text-white mx-1 px-2.5 py-0.5 rounded font-secondary text-p2'>
												{' '}
												{data &&
													data.current_inventory_cost.toLocaleString('en-US', {
														style: 'currency',
														currency: 'USD',
														maximumFractionDigits: 0,
													})}
											</span>
										</>
									) : (
										<span className='font-semibold mr-4'>This facility does not have any periods to analyze.</span>
									)}
								</p>
							</div>
							<div className='flex flex-col xl:flex-row gap-8 my-8'>
								<div className='basis-full bg-gray-50 rounded-b-md'>
									{' '}
									<div
										className='h-32 flex justify-between gap-3 p-3 bg-bluegray-900 text-white rounded-sm cursor-pointer'
										onClick={() => navigate(Routes.PAR_LEVEL_INVENTORY)}
									>
										<div>
											<div className='w-100'>
												<div className='font-preset-label-white uppercase font-secondary text-p2'>Par Level Optimization</div>
											</div>
											<div className='flex gap-2 mt-1 mb-2'>
												{' '}
												{data &&
													data.par_level_optimization.toLocaleString('en-US', {
														style: 'currency',
														currency: 'USD',
														maximumFractionDigits: 0,
													})}
											</div>
											<div className='flex'>
												<div className={'text-green-500'}>
													<div className='material-symbol-[1rem]'>arrow_right</div>
												</div>
												<div className='font-secondary text-[0.8em]'>Click to view more</div>
											</div>
										</div>
										<div className='flex gap-2'>
											<span className='material-symbols-outlined'>escalator</span>
										</div>
									</div>
								</div>
								<div className='basis-full bg-gray-50 rounded-b-md'>
									{' '}
									<div
										className='h-32 flex items-left justify-between gap-3 p-3 bg-bluegray-900 text-white rounded-sm cursor-pointer'
										onClick={() => navigate(Routes.OBSOLETE_INVENTORY)}
									>
										<div className=''>
											<div className='font-preset-label-white uppercase font-secondary text-p2'>Obsolete Inventory</div>
											<div className='flex gap-2 mt-1 mb-2'>
												{' '}
												{data &&
													data.obsolete_inventory.toLocaleString('en-US', {
														style: 'currency',
														currency: 'USD',
														maximumFractionDigits: 0,
													})}
											</div>
											<div className='flex'>
												<div className={'text-green-500'}>
													<div className='material-symbol-[1rem]'>arrow_right</div>
												</div>
												<div className='font-secondary text-[0.8em]'>Click to view more</div>
											</div>
										</div>
										<div className='flex gap-2'>
											<span className='material-symbols-outlined'>clock_arrow_up</span>
										</div>
									</div>
								</div>
								<div className='basis-full bg-gray-50 rounded-b-md'>
									{' '}
									<div className='h-32 flex items-left justify-between gap-3 p-3 bg-bluegray-900 text-white rounded-sm'>
										<div className=''>
											<div className='font-preset-label-white uppercase font-secondary text-p2'>Missed Charges</div>
											<div className='flex gap-2 mt-1 mb-2'>
												{data &&
													data.missed_charges.toLocaleString('en-US', {
														style: 'currency',
														currency: 'USD',
														maximumFractionDigits: 0,
													})}
											</div>
										</div>
										<div className='flex gap-2'>
											<span className='material-symbols-outlined'>price_check</span>
										</div>
									</div>
								</div>
								<div className='basis-full bg-gray-50 rounded-b-md'>
									{' '}
									<div className='h-32 flex items-left justify-between gap-3 p-3 bg-bluegray-900 text-white rounded-sm'>
										<div className=''>
											<div className='font-preset-label-white uppercase font-secondary text-p2'>Inventory Turns</div>
											<div className='flex gap-2 mt-1 mb-2'>{data && data.inventory_turns} Turns</div>
										</div>
										<div className='flex gap-2'>
											<span className='material-symbols-outlined'>event_busy</span>
										</div>
									</div>
								</div>
							</div>
							<div className='flex flex-col xl:flex-row gap-8 my-8'>
								<div className='basis-full bg-gray-50 rounded-b-md'>
									{' '}
									<div className='h-32 flex items-left justify-between gap-3 p-3 bg-gray-500 text-white rounded-sm'>
										<div className=''>
											<div className='font-preset-label-white uppercase font-secondary text-p2'>Off Contract Spend</div>
											<div className='flex gap-2 mt-1 mb-2'>$1,064,095.75</div>
										</div>
										<div className='flex gap-2'>
											<span className='material-symbols-outlined'>request_quote</span>
										</div>
									</div>
								</div>
								<div className='basis-full bg-gray-50 rounded-b-md'>
									{' '}
									<div className='h-32 flex items-left justify-between gap-3 p-3 bg-gray-500 text-white rounded-sm'>
										<div className=''>
											<div className='font-preset-label-white uppercase font-secondary text-p2'>
												Benchmark Pricing Optimization
											</div>
											<div className='flex gap-2 mt-1 mb-2'>$510,343.75</div>
										</div>
										<div className='flex gap-2'>
											<span className='material-symbols-outlined'>toll</span>
										</div>
									</div>
								</div>
								<div className='basis-full bg-gray-50 rounded-b-md'>
									{' '}
									<div className='h-32 flex items-left justify-between gap-3 p-3 bg-gray-500 text-white rounded-sm'>
										<div className=''>
											<div className='font-preset-label-white uppercase font-secondary text-p2'>Expired Inventory</div>
											<div className='flex gap-2 mt-1 mb-2'>$132,467</div>
										</div>
										<div className='flex gap-2'>
											<span className='material-symbols-outlined'>inventory_2</span>
										</div>
									</div>
								</div>
								<div className='basis-full bg-gray-50 rounded-b-md'>
									{' '}
									<div className='h-32 flex items-left justify-between gap-3 p-3 bg-gray-500 text-white rounded-sm'>
										<div className=''>
											<div className='font-preset-label-white uppercase font-secondary text-p2'>Freight Expenses</div>
											<div className='flex gap-2 mt-1 mb-2'>$20,107</div>
										</div>
										<div className='flex gap-2'>
											<span className='material-symbols-outlined'>trolley</span>
										</div>
									</div>
								</div>
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
}

interface ChartHeadingProps {
	/** Title of the heading to render. */
	title: string;
	subtitle?: string | undefined;
	/** Description to render in the tooltip. */
	description: string;
}

function ChartHeading({ title, subtitle = undefined, description }: ChartHeadingProps) {
	return (
		<div className='justify-start items-center'>
			<div className='flex justify-between'>
				<h4 className='font-semibold mr-3 mb-1'>{title}</h4>
				<Tooltip content={description}>
					<span className='text-blue-500 material-symbol'>info</span>
				</Tooltip>
			</div>
			{subtitle && <p className='text-[0.75em]'>{subtitle}</p>}
		</div>
	);
}

export default SupplyChainDashboard;
